/**
 * Live mid prices for every coin (perps, spot and HIP-4 outcome coins "#<enc>").
 *
 * Source: REST `allMids` for the first snapshot, then the documented `allMids` WS stream (full map every
 * ~5 s, uncompressed, includes outcome coins; verified on testnet: 1336 "#…" keys = every side of every
 * outcome). Each WS frame replaces the cached map, so a reconnect needs no REST re-sync.
 * Shared hook: the catalog uses it for tile prices; the market page may use it for the header price.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { coin, type Side } from './outcomes';
import { hlQueryKey, useHlEnv, useInfoClient, useSubscription } from './react';
import type { AllMidsResponse } from './types';

const ALL_MIDS_SUB = { type: 'allMids' } as const;

export function allMidsQueryKey(env: Parameters<typeof hlQueryKey>[0]): unknown[] {
  return hlQueryKey(env, 'allMids');
}

export interface AllMidsResult {
  /** coin -> mid as a decimal string; undefined until the first snapshot. */
  mids: AllMidsResponse | undefined;
  isLoading: boolean;
  error: unknown;
}

/** All mids, kept live by the `allMids` WS stream. Pass enabled=false to skip (no fetch, no subscription). */
export function useAllMids(enabled = true): AllMidsResult {
  const env = useHlEnv();
  const info = useInfoClient();
  const qc = useQueryClient();
  const key = allMidsQueryKey(env);
  const q = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => info.allMids({ signal }),
    enabled,
    // The WS stream keeps it fresh; never refetch on our own while subscribed.
    staleTime: Infinity,
  });
  useSubscription(enabled ? ALL_MIDS_SUB : null, (data) => {
    if (data && typeof data.mids === 'object') qc.setQueryData(key, data.mids);
  });
  return { mids: q.data, isLoading: q.isLoading, error: q.error };
}

/** Mid of one outcome side as a number in (0, 1), or undefined when there is no mid. */
export function outcomeMid(mids: AllMidsResponse | undefined, outcome: number, side: Side): number | undefined {
  if (!mids) return undefined;
  const raw = mids[coin(outcome, side)];
  if (raw === undefined) return undefined;
  const v = Number(raw);
  return Number.isFinite(v) ? v : undefined;
}
